<template>
  <SoDialog v-model="value" card-title="Meter Photos" :max-width="1250">
    <div class="d-flex justify-md-space-between">
      <div
        class="mb-5 flex-0-0 photo-history__image-wrapper"
        :class="{ 'photo-history__image--placeholder': !requestedPhotoName }"
      >
        <template v-if="requestedPhotoName && !isPhotoLoading">
          <SoImage
            :src="requestedPhotoUrl"
            :alt="requestedPhotoName"
            width="286"
            class="photo-history__image"
            @click="isImagePreviewOpen = true"
          />
          <div
            class="d-flex justify-space-between photo-history__description mt-3"
          >
            <span class="dl-vertical">{{ requestedPhotoName }}</span>
            <SoButton title="Download" download :href="requestedPhotoUrl" />
          </div>
        </template>
        <template v-else-if="requestedPhotoName && isPhotoLoading">
          <SoSkeleton height="252px" width="286px" class="mb-5" />
        </template>
        <template v-else> No image selected </template>
      </div>
      <PhotoRequestWidget class="flex-0-0 photo-history__request" />
    </div>
    <div>
      <SoDataTable
        ref="photo-history-table"
        :headers="headers"
        :items="photoHistoryData"
        has-pagination
        :searchable="false"
        :default-items-per-page="10"
        :sort-by="[{ key: 'sortDate', order: 'desc' }]"
        :default-items-per-page-options="tablePaginationOptions"
      >
        <template #item.source="{ internalItem }">
          <span
            v-if="internalItem.raw.otherAccountLabel"
            class="photo-history__prev-account"
          >
            {{ internalItem.raw.otherAccountLabel }}
            <router-link
              :to="{
                name: RouteNames.CustomerOverview,
                params: { accountNumber: internalItem.raw.accountNumber },
              }"
              target="_blank"
            >
              {{ internalItem.raw.accountNumber }}
            </router-link>
          </span>
          {{
            internalItem.raw.installer
              ? `${internalItem.raw.installer} ${internalItem.raw.source}`
              : internalItem.raw.source
          }}
        </template>
        <template #item.failedRead="{ internalItem }">
          <template v-if="typeof internalItem.columns.failedRead === 'string'">
            {{ internalItem.columns.failedRead }}
          </template>
          <template v-else-if="internalItem.columns.failedRead.length > 1">
            <p>Day: {{ internalItem.columns.failedRead[0] }}</p>
            <p>Night: {{ internalItem.columns.failedRead[1] }}</p>
          </template>
          <template v-else>{{ internalItem.columns.failedRead[0] }}</template>
        </template>
        <template #item.viewImage="{ internalItem }">
          <div class="photo-history__view-image-cell">
            <div
              v-for="(image, index) in internalItem.columns.viewImage"
              :key="index"
              class="photo-history__view-image-link"
            >
              <SoLink
                :text="image.displayName"
                @click="displayImage(image, internalItem.columns.date)"
              />
            </div>
          </div>
        </template>
      </SoDataTable>
      <div
        v-if="otherAccounts.length && currentSettled"
        class="photo-history__site-status"
      >
        <template v-if="isLoadingOtherAccounts">
          <SoProgress indeterminate :size="16" />
          <span>
            Photos on {{ otherSettledCount }}/{{ otherAccounts.length }} other
            accounts at this site loaded…
          </span>
        </template>
        <template v-else>
          <SoIcon icon="check" color="success" />
          <span>
            Showing photos from this account and
            {{ otherAccounts.length }} other
            {{ otherAccounts.length === 1 ? "account" : "accounts" }} at this
            site
          </span>
        </template>
      </div>
      <SiteAccountPhotoRows
        v-for="account in visibleOtherAccounts"
        :key="account.number"
        :account-number="account.number"
        :relation-label="relationLabelFor(account)"
        @update:rows="(rows) => onOtherAccountRows(account.number, rows)"
        @settled="onOtherAccountSettled"
      />
    </div>
  </SoDialog>
  <SoDialog
    v-model="isImagePreviewOpen"
    :shrinkable="true"
    :card-title="requestedPhotoName"
    class="photo-history__preview"
  >
    <SoImage
      class="photo-history__preview-img"
      :src="requestedPhotoUrl"
      :alt="requestedPhotoName"
      width="auto"
    />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoImage from "@/components/SoImage.vue"
  import PhotoRequestWidget from "../widgets/PhotoRequestWidget.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { MeterReadingsService } from "@/services/operations/MeterReadingsService"
  import { MeterAppointmentService } from "@/services/MeterAppointmentService"
  import {
    appointmentsToPhotoItems,
    tagPhotoItemsForAccount,
  } from "@/models/meterPhotos/meterPhotos.parser"
  import { IMeterReadingPhotoItem } from "@/models/meterPhotos/meterPhotos.types"
  import { IAccount } from "@/models/account/account.types"
  import { externalRoutes } from "@/constants/routes/externalRoutes"
  import { RouteNames } from "@/types/router"
  import SoLink from "@/components/SoLink.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SiteAccountPhotoRows from "../components/SiteAccountPhotoRows.vue"

  const headers = [
    {
      title: "Uploaded Date",
      key: "date",
      sortable: true,
    },
    {
      title: "Source",
      key: "source",
      sortable: false,
    },
    {
      title: "Fuel",
      key: "fuel",
      sortable: false,
    },
    {
      title: "Meterpoints",
      key: "meterPoint",
      sortable: false,
    },
    {
      title: "Failed Read",
      key: "failedRead",
      sortable: false,
    },
    {
      title: "Request Reason",
      key: "requestReason",
      sortable: false,
    },
    {
      title: "Error Code",
      key: "errorCode",
      sortable: false,
    },
    {
      title: "View image",
      key: "viewImage",
      sortable: false,
    },
  ]

  export default defineComponent({
    components: {
      SoDialog,
      SoDataTable,
      SoButton,
      SoSkeleton,
      SoLink,
      SoImage,
      SoProgress,
      SoIcon,
      PhotoRequestWidget,
      SiteAccountPhotoRows,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      const currentAccountStore = useCurrentAccountStore()
      return {
        headers,
        RouteNames,
        currentAccountNumber: currentAccountStore.accountNumber,
        photoHistoryQuery: MeterReadingsService.getPhotoEvidence(
          currentAccountStore.accountNumber,
        ),
        coreIdQuery: currentAccountStore.coreId,
        otherAccountRows: {} as Record<string, IMeterReadingPhotoItem[]>,
        otherSettledCount: 0,
        requestedPhotoName: "",
        requestedPhotoUrl: "",
        requestedPhotoDate: "",
        requestedPhotoPath: "",
        isImagePreviewOpen: false,
        isPhotoLoading: false,
        externalRoutes,
        tablePaginationOptions: [
          { value: 10, title: "10" },
          { value: 25, title: "25" },
        ],
      }
    },
    computed: {
      // Read as a computed (not captured in data()) so it tracks the store
      // getter: the accounts-at-site read only starts once the viewed account's
      // junifer property id has loaded, which re-runs the getter and yields a
      // fresh aggregate the one-time data() snapshot would have missed.
      supplyAddressAccountsQuery() {
        return useCurrentAccountStore().supplyAddressAccounts
      },
      appointmentsQuery() {
        if (!this.coreIdQuery?.data) return null
        return MeterAppointmentService.getAppointments(this.coreIdQuery.data)
      },
      siteVisitPhotos() {
        if (!this.appointmentsQuery?.data) return []
        return appointmentsToPhotoItems(this.appointmentsQuery.data)
      },
      currentAccountRows(): IMeterReadingPhotoItem[] {
        const customerPhotos =
          this.photoHistoryQuery && !this.photoHistoryQuery.isFetching
            ? this.photoHistoryQuery.data
            : []
        return tagPhotoItemsForAccount(
          [...(customerPhotos || []), ...this.siteVisitPhotos],
          { accountNumber: this.currentAccountNumber },
        )
      },
      currentSettled(): boolean {
        return (
          !this.photoHistoryQuery?.isFetching &&
          !this.appointmentsQuery?.isFetching
        )
      },
      // Start date of the viewed account — the pivot for labelling other
      // accounts as 'Newer' or 'Previous'. Infinity if not found, so everything
      // falls back to 'Previous account'.
      currentAccountStartValue(): number {
        const accounts = this.supplyAddressAccountsQuery?.data ?? []
        const current = accounts.find(
          (account) => account.number === this.currentAccountNumber,
        )
        return current?.startDate?.valueOf() ?? Infinity
      },
      // All other accounts that have held this site's meter point(s), ordered by
      // account start date (newest first) — this is the load order; the table
      // itself still sorts every row by photo date desc.
      otherAccounts(): IAccount[] {
        const accounts = this.supplyAddressAccountsQuery?.data ?? []
        return [...accounts]
          .filter((account) => account.number !== this.currentAccountNumber)
          .sort(
            (a, b) =>
              (b.startDate ?? b.endDate).valueOf() -
              (a.startDate ?? a.endDate).valueOf(),
          )
      },
      // Reveal one other account at a time: child N only mounts (and fetches)
      // once the current account and other accounts N-1 have settled.
      visibleOtherAccounts(): IAccount[] {
        if (!this.currentSettled) return []
        return this.otherAccounts.slice(0, this.otherSettledCount + 1)
      },
      isLoadingOtherAccounts(): boolean {
        return (
          this.currentSettled &&
          this.otherSettledCount < this.otherAccounts.length
        )
      },
      photoHistoryData(): IMeterReadingPhotoItem[] {
        const otherRows = Object.values(this.otherAccountRows).flat()
        return [...this.currentAccountRows, ...otherRows]
      },
      requestedPhotoQuery() {
        if (!this.requestedPhotoPath) return null
        return MeterReadingsService.getPhotoUrl(this.requestedPhotoPath)
      },
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    watch: {
      requestedPhotoQuery: {
        deep: true,
        handler(result) {
          if (result?.data) {
            this.requestedPhotoUrl = result.data
            this.isPhotoLoading = false
          }
        },
      },
    },
    methods: {
      // Other accounts that started after the viewed account are 'Newer'; those
      // that started before (or at the same time) are 'Previous'.
      relationLabelFor(account: IAccount): string {
        const startValue = (account.startDate ?? account.endDate).valueOf()
        return startValue > this.currentAccountStartValue
          ? "Newer account"
          : "Previous account"
      },
      onOtherAccountRows(
        accountNumber: string,
        rows: IMeterReadingPhotoItem[],
      ) {
        this.otherAccountRows[accountNumber] = rows
      },
      onOtherAccountSettled() {
        if (this.otherSettledCount < this.otherAccounts.length) {
          this.otherSettledCount += 1
        }
      },
      async displayImage(image, date) {
        if (image.directUrl) {
          this.requestedPhotoName = image.displayName
          this.requestedPhotoUrl = image.directUrl
          this.requestedPhotoPath = ""
          this.requestedPhotoDate = date
          this.isPhotoLoading = false
          return
        }
        if (this.requestedPhotoPath !== image.path) {
          this.requestedPhotoName = image.displayName
          this.requestedPhotoPath = image.path
          this.isPhotoLoading = true
          this.requestedPhotoDate = date
        }
      },
    },
  })
</script>

<style scoped>
  .photo-history__image-wrapper {
    max-width: 286px;
    min-height: 252px;
  }
  .photo-history__request {
    width: 440px;
  }
  .photo-history__description {
    align-items: flex-start;
  }
  .photo-history__description span {
    flex: 1;
    word-break: break-word;
  }
  .photo-history__view-image-cell {
    min-width: 200px;
  }
  .photo-history__view-image-link {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .photo-history__view-image-link + .photo-history__view-image-link {
    margin-top: 4px;
  }
  .photo-history__image--placeholder {
    width: 286px;
    height: 252px;
    background-color: rgb(var(--v-theme-border));
    align-content: center;
    text-align: center;
  }
  .photo-history__image {
    width: 286px;
    height: 200px;
    object-fit: cover;
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .photo-history__preview-img {
    width: 100%;
  }
  .photo-history__prev-account {
    display: block;
    font-size: 12px;
    color: rgb(var(--v-theme-muted, 110, 110, 128));
  }
  .photo-history__site-status {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 8px 14px;
    font-size: 13px;
    color: rgb(var(--v-theme-muted, 110, 110, 128));
  }
</style>
